import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Slider,
  Autocomplete,
  createFilterOptions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Campaign as ReleaseIcon,
  LocalOffer as TagIcon,
  Article as ArticleIcon,
  Visibility as ActiveIcon,
  VisibilityOff as InactiveIcon,
  CalendarMonth as DateIcon,
  Speed as ThresholdIcon,
  AutoMode as AutoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import PlatformDialog from '../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../components/molecules/PlatformDialog/PlatformDialogTitle';
import { operationalScrollbars } from '../design-system/tokens';

const filter = createFilterOptions();

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

// Função para converter data para formato de input
const toInputDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

/**
 * Página de Releases - Gerenciamento de comunicados da marca
 *
 * Permite ao usuário:
 * - Criar releases com título, conteúdo e data
 * - Associar tags que serão aplicadas automaticamente aos artigos similares
 * - Configurar threshold de similaridade
 * - Escolher entre aplicação automática ou manual das tags
 */
const ReleasesPage = () => {
  // Estados
  const [releases, setReleases] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados para dialog de release
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    title: '',
    content: '',
    releaseDate: new Date(),
    tags: [],
    threshold: 70,
    autoApply: true
  });

  // Estados para estatísticas
  const [stats, setStats] = useState(null);

  // Estado para controlar o input do Autocomplete de tags
  const [tagInputValue, setTagInputValue] = useState('');

  // Carregar releases
  const loadReleases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/releases');
      setReleases(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar releases:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar releases',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar tags disponíveis
  const loadTags = useCallback(async () => {
    try {
      const response = await api.get('/tags');
      setTags(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  }, []);

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/releases/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  useEffect(() => {
    loadReleases();
    loadTags();
    loadStats();
  }, [loadReleases, loadTags, loadStats]);

  // Abrir dialog para criar
  const handleOpenCreate = () => {
    setEditingRelease(null);
    setReleaseForm({
      title: '',
      content: '',
      releaseDate: toInputDate(new Date()),
      tags: [],
      threshold: 70,
      autoApply: true
    });
    setDialogOpen(true);
  };

  // Abrir dialog para editar
  const handleOpenEdit = (release) => {
    setEditingRelease(release);
    setReleaseForm({
      title: release.title,
      content: release.content,
      releaseDate: toInputDate(release.releaseDate),
      tags: release.tags?.map(t => t._id) || [],
      threshold: release.threshold || 70,
      autoApply: release.autoApply !== undefined ? release.autoApply : true
    });
    setDialogOpen(true);
  };

  // Salvar release
  const handleSave = async () => {
    if (!releaseForm.title.trim() || !releaseForm.content.trim()) {
      setSnackbar({ open: true, message: 'Título e conteúdo são obrigatórios', severity: 'warning' });
      return;
    }

    try {
      setSaving(true);

      if (editingRelease) {
        // Atualizar
        await api.put(`/releases/${editingRelease._id}`, releaseForm);
        setSnackbar({ open: true, message: 'Release atualizado!', severity: 'success' });
      } else {
        // Criar
        await api.post('/releases', releaseForm);
        setSnackbar({ open: true, message: 'Release criado!', severity: 'success' });
      }

      setDialogOpen(false);
      loadReleases();
      loadStats();
    } catch (error) {
      console.error('Erro ao salvar release:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao salvar release',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Deletar release
  const handleDelete = async (releaseId) => {
    if (!window.confirm('Deseja realmente deletar este release?')) return;

    try {
      await api.delete(`/releases/${releaseId}`);
      setSnackbar({ open: true, message: 'Release deletado', severity: 'success' });
      loadReleases();
      loadStats();
    } catch (error) {
      console.error('Erro ao deletar release:', error);
      setSnackbar({ open: true, message: 'Erro ao deletar release', severity: 'error' });
    }
  };

  // Toggle ativo/inativo
  const handleToggleActive = async (releaseId) => {
    try {
      await api.put(`/releases/${releaseId}/toggle-active`);
      loadReleases();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setSnackbar({ open: true, message: 'Erro ao alterar status', severity: 'error' });
    }
  };

  // Criar nova tag inline
  const handleCreateTag = async (tagName) => {
    if (!tagName || !tagName.trim()) return;

    try {
      const response = await api.post('/tags', { name: tagName.trim() });
      const newTag = response.data.data;

      // Adicionar a nova tag à lista
      setTags(prev => [...prev, newTag]);

      // Adicionar automaticamente ao release atual
      setReleaseForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag._id]
      }));

      // Limpar o input do Autocomplete
      setTagInputValue('');

      // Dispara evento para atualizar outras páginas
      window.dispatchEvent(new CustomEvent('tags-updated'));

      setSnackbar({ open: true, message: `Tag "${newTag.name}" criada!`, severity: 'success' });
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao criar tag',
        severity: 'error'
      });
    }
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReleaseIcon color="primary" />
            Releases
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie releases/comunicados da marca para matching automático com artigos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Novo Release
        </Button>
      </Box>

      {/* Estatísticas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">{stats.total || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.active || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Ativos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.totalMatches || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Matches</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.totalPending || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Pendentes</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Lista de releases */}
      {releases.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ReleaseIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum release cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crie um release para que artigos similares recebam tags automaticamente.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Criar Primeiro Release
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {releases.map((release) => (
            <Grid item xs={12} md={6} key={release._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: release.isActive ? 1 : 0.6,
                  borderLeft: 4,
                  borderColor: release.isActive ? 'primary.main' : 'grey.400'
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  {/* Header do card */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ flex: 1, mr: 1 }}>
                      {release.title}
                    </Typography>
                    <Chip
                      label={release.isActive ? 'Ativo' : 'Inativo'}
                      color={release.isActive ? 'success' : 'default'}
                      size="small"
                      icon={release.isActive ? <ActiveIcon /> : <InactiveIcon />}
                    />
                  </Box>

                  {/* Data */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <DateIcon fontSize="inherit" />
                    {formatDate(release.releaseDate)}
                  </Typography>

                  {/* Conteúdo (truncado) */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {release.content}
                  </Typography>

                  {/* Tags */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {release.tags?.map((tag) => (
                      <Chip
                        key={tag._id}
                        label={tag.name}
                        size="small"
                        icon={<TagIcon />}
                        sx={{
                          bgcolor: tag.color ? `${tag.color}20` : undefined,
                          borderColor: tag.color,
                          color: tag.color
                        }}
                        variant="outlined"
                      />
                    ))}
                    {(!release.tags || release.tags.length === 0) && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma tag associada
                      </Typography>
                    )}
                  </Box>

                  {/* Configurações */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Tooltip title="Threshold de similaridade">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ThresholdIcon fontSize="small" color="action" />
                        <Typography variant="caption">{release.threshold}%</Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title={release.autoApply ? 'Aplicação automática' : 'Aprovação manual'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AutoIcon fontSize="small" color={release.autoApply ? 'success' : 'action'} />
                        <Typography variant="caption">
                          {release.autoApply ? 'Auto' : 'Manual'}
                        </Typography>
                      </Box>
                    </Tooltip>
                    {release.stats?.matchedArticles > 0 && (
                      <Tooltip title="Artigos com match">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArticleIcon fontSize="small" color="info" />
                          <Typography variant="caption">{release.stats.matchedArticles}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>

                <Divider />

                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title={release.isActive ? 'Desativar' : 'Ativar'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(release._id)}
                        color={release.isActive ? 'default' : 'success'}
                      >
                        {release.isActive ? <InactiveIcon /> : <ActiveIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(release)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(release._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dicas */}
      <Alert severity="info" sx={{ mt: 4 }} icon={<ReleaseIcon />}>
        <Typography variant="subtitle2" gutterBottom>
          Como funciona o matching de releases:
        </Typography>
        <Typography variant="body2">
          1. Quando um novo artigo é adicionado, ele é comparado com todos os releases ativos.
          <br />
          2. Se a similaridade for maior que o threshold, as tags do release são aplicadas ao artigo.
          <br />
          3. Com <strong>Auto</strong>, as tags são aplicadas automaticamente. Com <strong>Manual</strong>, ficam pendentes de aprovação.
        </Typography>
      </Alert>

      {/* Dialog de Criação/Edição (Padronizado) */}
      <PlatformDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <PlatformDialogTitle
          title={editingRelease ? 'Editar Release' : 'Novo Release'}
          subtitle="Configure o comunicado para matching automático de similaridade."
          icon={<ReleaseIcon />}
          onClose={() => setDialogOpen(false)}
        />

        <DialogContent sx={{ pt: 3, pb: 2, ...operationalScrollbars.default }}>
          <Grid container spacing={3}>
            {/* Título */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título do Release"
                placeholder="Ex: Lançamento do novo produto X"
                value={releaseForm.title}
                onChange={(e) => setReleaseForm({ ...releaseForm, title: e.target.value })}
                required
                autoFocus
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Conteúdo */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Conteúdo"
                placeholder="Texto completo do release/comunicado..."
                value={releaseForm.content}
                onChange={(e) => setReleaseForm({ ...releaseForm, content: e.target.value })}
                required
                multiline
                rows={6}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Data */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data do Release"
                type="date"
                value={releaseForm.releaseDate}
                onChange={(e) => setReleaseForm({ ...releaseForm, releaseDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                ListboxProps={{ sx: operationalScrollbars.thin }}
                inputValue={tagInputValue}
                onInputChange={(_, newInputValue) => setTagInputValue(newInputValue)}
                options={tags.filter(t => !releaseForm.tags.includes(t._id))}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.name;
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  // Se há texto digitado, mostrar opção de criar nova tag
                  if (params.inputValue !== '') {
                    const inputLower = params.inputValue.toLowerCase().trim();
                    const exists = tags.some(
                      tag => tag.name.toLowerCase() === inputLower
                    );
                    if (!exists && inputLower.length > 0) {
                      filtered.push({
                        inputValue: params.inputValue.trim(),
                        name: `Criar "${params.inputValue.trim()}"`,
                        isNew: true
                      });
                    }
                  }
                  return filtered;
                }}
                value={tags.filter(t => releaseForm.tags.includes(t._id))}
                onChange={(_, newValue) => {
                  // Verificar se o último item é uma nova tag ou string
                  const lastItem = newValue[newValue.length - 1];
                  
                  // Se for string, criar nova tag
                  if (typeof lastItem === 'string') {
                    handleCreateTag(lastItem);
                    newValue = newValue.slice(0, -1);
                  } else if (lastItem && lastItem.isNew) {
                    handleCreateTag(lastItem.inputValue);
                    newValue = newValue.slice(0, -1);
                  }
                  setReleaseForm({
                    ...releaseForm,
                    tags: newValue.filter(t => t._id).map(t => t._id)
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags a aplicar"
                    placeholder="Buscar ou criar..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key || option._id || option.inputValue} {...otherProps}>
                      {option.isNew ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon fontSize="small" color="primary" />
                          <Typography color="primary">{option.name}</Typography>
                        </Box>
                      ) : (
                        option.name
                      )}
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      label={option.name}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        bgcolor: option.color ? `${option.color}20` : undefined,
                        borderColor: option.color
                      }}
                      variant="outlined"
                    />
                  ))
                }
              />
            </Grid>

            {/* Threshold */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
                  <ThresholdIcon fontSize="small" color="primary" />
                  Sensibilidade: {releaseForm.threshold}%
                </Typography>
                <Slider
                  value={releaseForm.threshold}
                  onChange={(_, value) => setReleaseForm({ ...releaseForm, threshold: value })}
                  min={30}
                  max={100}
                  step={5}
                  marks={[
                    { value: 30, label: '30%' },
                    { value: 70, label: '70%' },
                    { value: 100, label: '100%' }
                  ]}
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  💡 Dica: Títulos com {releaseForm.threshold}% de similaridade geram match automático.
                </Typography>
              </Box>
            </Grid>

            {/* Auto Apply */}
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: releaseForm.autoApply ? 'success.50' : 'grey.50', borderColor: releaseForm.autoApply ? 'success.100' : 'grey.200' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={releaseForm.autoApply}
                      onChange={(e) => setReleaseForm({ ...releaseForm, autoApply: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Aplicação Automática</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {releaseForm.autoApply
                          ? 'As tags serão aplicadas sem revisão'
                          : 'As tags ficarão aguardando aprovação'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !releaseForm.title.trim() || !releaseForm.content.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            {saving ? 'Processando...' : (editingRelease ? 'Salvar Alterações' : 'Criar Release')}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Snackbar */}
      <ModernToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        duration={4000}
        position="bottom-center"
      />
    </Container>
  );
};

export default ReleasesPage;
